import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  TextField,
  TextArea,
  Switch,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { Check, Plus } from 'lucide-react';

import {
  createProcessingTypeAtom,
  updateProcessingTypeAtom,
} from '../../_graphql/mutationProcessingType';
import { useTranslations } from '../../i18n/useTranslations';
import { ProcessingTypeRecord } from '../../types/processingType';

import {
  ProcessingTypeInputItemData,
  ProcessingTypeInputItemRow,
} from './ProcessingTypeInputItemRow';
import {
  ProcessingTypeOutputItemData,
  ProcessingTypeOutputItemRow,
} from './ProcessingTypeOutputItemRow';

type ProcessingTypeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingProcessingType?: ProcessingTypeRecord | null;
};

function generateId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export const ProcessingTypeDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingProcessingType,
}: ProcessingTypeDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [name, setName] = React.useState('');
  const [code, setCode] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [inputItems, setInputItems] = React.useState<
    ProcessingTypeInputItemData[]
  >([]);
  const [outputItems, setOutputItems] = React.useState<
    ProcessingTypeOutputItemData[]
  >([]);

  const createProcessingType = useSetAtom(createProcessingTypeAtom({}));
  const updateProcessingType = useSetAtom(updateProcessingTypeAtom({}));

  const isEditMode = !!editingProcessingType;

  React.useEffect(() => {
    if (editingProcessingType) {
      setName(editingProcessingType.name);
      setCode(editingProcessingType.code);
      setDescription(editingProcessingType.description || '');
      setIsActive(editingProcessingType.isActive);

      if (editingProcessingType.inputItems) {
        setInputItems(
          editingProcessingType.inputItems.map((item) => ({
            id: generateId(),
            existingId: item.id,
            itemId: item.itemId,
            isPrimary: item.isPrimary,
            sortOrder: item.sortOrder,
            notes: item.notes || undefined,
          })),
        );
      } else {
        setInputItems([]);
      }

      if (editingProcessingType.outputItems) {
        setOutputItems(
          editingProcessingType.outputItems.map((item) => ({
            id: generateId(),
            existingId: item.id,
            itemId: item.itemId,
            outputUnit: item.outputUnit,
            yieldPercentage: item.yieldPercentage?.toString() || '',
            isPrimary: item.isPrimary,
            isNoCost: item.isNoCost,
            costAllocationPercentage:
              item.costAllocationPercentage?.toString() || undefined,
            sortOrder: item.sortOrder,
            notes: item.notes || undefined,
          })),
        );
      } else {
        setOutputItems([]);
      }
    } else {
      setName('');
      setCode('');
      setDescription('');
      setIsActive(true);
      setInputItems([]);
      setOutputItems([]);
    }
  }, [editingProcessingType]);

  const handleClose = React.useCallback(() => {
    setName('');
    setCode('');
    setDescription('');
    setIsActive(true);
    setInputItems([]);
    setOutputItems([]);
    onClose();
  }, [onClose]);

  const handleAddInput = React.useCallback(() => {
    setInputItems((prev) => [
      ...prev,
      {
        id: generateId(),
        itemId: '',
        isPrimary: prev.length === 0,
        sortOrder: prev.length,
      },
    ]);
  }, []);

  const handleUpdateInput = React.useCallback(
    (index: number, data: Partial<ProcessingTypeInputItemData>) => {
      setInputItems((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], ...data };

        if (data.isPrimary === true) {
          return updated.map((item, i) => ({
            ...item,
            isPrimary: i === index,
          }));
        }

        return updated;
      });
    },
    [],
  );

  const handleRemoveInput = React.useCallback((index: number) => {
    setInputItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddOutput = React.useCallback(() => {
    setOutputItems((prev) => [
      ...prev,
      {
        id: generateId(),
        itemId: '',
        outputUnit: enumUnit.Pounds as Unit,
        yieldPercentage: '',
        isPrimary: prev.length === 0,
        isNoCost: false,
        sortOrder: prev.length,
      },
    ]);
  }, []);

  const handleUpdateOutput = React.useCallback(
    (index: number, data: Partial<ProcessingTypeOutputItemData>) => {
      setOutputItems((prev) => {
        const updated = [...prev];
        updated[index] = { ...updated[index], ...data };

        if (data.isPrimary === true) {
          return updated.map((item, i) => ({
            ...item,
            isPrimary: i === index,
          }));
        }

        return updated;
      });
    },
    [],
  );

  const handleRemoveOutput = React.useCallback((index: number) => {
    setOutputItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const validateRecipe = React.useCallback(() => {
    if (inputItems.length === 0 && outputItems.length === 0) {
      return true;
    }

    for (const item of inputItems) {
      if (!item.itemId) {
        toast.error(
          t.processingTypesView.dialog.recipe.validation.inputItemRequired,
        );
        return false;
      }
    }

    for (const item of outputItems) {
      if (!item.itemId) {
        toast.error(
          t.processingTypesView.dialog.recipe.validation.outputItemRequired,
        );
        return false;
      }
      if (
        !item.yieldPercentage ||
        parseFloat(item.yieldPercentage) <= 0 ||
        parseFloat(item.yieldPercentage) > 100
      ) {
        toast.error(
          t.processingTypesView.dialog.recipe.validation
            .yieldPercentageRequired,
        );
        return false;
      }
    }

    if (inputItems.length > 0) {
      const primaryInputCount = inputItems.filter((i) => i.isPrimary).length;
      if (primaryInputCount !== 1) {
        toast.error(
          t.processingTypesView.dialog.recipe.validation.needsPrimaryInput,
        );
        return false;
      }
    }

    if (outputItems.length > 0) {
      const costBearingCount = outputItems.filter((o) => !o.isNoCost).length;
      if (costBearingCount === 0) {
        toast.error(
          t.processingTypesView.dialog.recipe.validation.noCostBearingOutputs,
        );
        return false;
      }
    }

    return true;
  }, [inputItems, outputItems, t, toast]);

  const handleSubmit = React.useCallback(async () => {
    if (!name.trim()) {
      toast.error(t.processingTypesView.dialog.validation.nameRequired);
      return;
    }

    if (!code.trim()) {
      toast.error(t.processingTypesView.dialog.validation.codeRequired);
      return;
    }

    if (!validateRecipe()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const inputItemsInput = inputItems.map((item, index) => ({
        id: item.existingId,
        itemId: item.itemId,
        isPrimary: item.isPrimary,
        sortOrder: index,
        notes: item.notes || undefined,
      }));

      const outputItemsInput = outputItems.map((item, index) => ({
        id: item.existingId,
        itemId: item.itemId,
        outputUnit: item.outputUnit,
        yieldPercentage: parseFloat(item.yieldPercentage),
        isPrimary: item.isPrimary,
        isNoCost: item.isNoCost,
        costAllocationPercentage: item.costAllocationPercentage
          ? parseFloat(item.costAllocationPercentage)
          : undefined,
        sortOrder: index,
        notes: item.notes || undefined,
      }));

      if (isEditMode && editingProcessingType) {
        await updateProcessingType({
          id: editingProcessingType.id,
          name: name.trim(),
          code: code.trim().toUpperCase(),
          description: description.trim() || undefined,
          isActive,
          inputItems: inputItemsInput.length > 0 ? inputItemsInput : undefined,
          outputItems:
            outputItemsInput.length > 0 ? outputItemsInput : undefined,
        });
        toast.success(t.processingTypesView.dialog.updateSuccess);
      } else {
        await createProcessingType({
          name: name.trim(),
          code: code.trim().toUpperCase(),
          description: description.trim() || undefined,
          isActive,
          inputItems: inputItemsInput.length > 0 ? inputItemsInput : undefined,
          outputItems:
            outputItemsInput.length > 0 ? outputItemsInput : undefined,
        });
        toast.success(t.processingTypesView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      toast.error(
        isEditMode
          ? t.processingTypesView.dialog.updateError(errorMessage)
          : t.processingTypesView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    code,
    description,
    isActive,
    inputItems,
    outputItems,
    isEditMode,
    editingProcessingType,
    createProcessingType,
    updateProcessingType,
    validateRecipe,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const canSubmit =
    Boolean(name.trim()) && Boolean(code.trim()) && !isSubmitting;

  const dialogTitle = isEditMode
    ? t.processingTypesView.dialog.editTitle
    : t.processingTypesView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.processingTypesView.dialog.updating
      : t.processingTypesView.dialog.update
    : isSubmitting
      ? t.processingTypesView.dialog.creating
      : t.processingTypesView.dialog.create;

  return (
    <ConsoleDialog
      className="@ProcessingTypeDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="fill"
      content={
        <div className="space-y-6">
          <div className="space-y-4">
            <TextField
              label={t.processingTypesView.dialog.nameLabel}
              value={name}
              onChange={setName}
              placeholder={t.processingTypesView.dialog.namePlaceholder}
              isRequired
            />

            <TextField
              label={t.processingTypesView.dialog.codeLabel}
              value={code}
              onChange={(val) => setCode(val.toUpperCase())}
              placeholder={t.processingTypesView.dialog.codePlaceholder}
              isRequired
            />

            <TextArea
              label={t.processingTypesView.dialog.descriptionLabel}
              value={description}
              onChange={setDescription}
              placeholder={t.processingTypesView.dialog.descriptionPlaceholder}
            />

            <div className="flex items-center gap-2">
              <Switch isSelected={isActive} onChange={setIsActive} />
              <span className="text-sm">
                {t.processingTypesView.dialog.isActiveLabel}
              </span>
            </div>
          </div>

          <div className="border-t border-neutral/20 pt-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-medium">
                {t.processingTypesView.dialog.recipe.inputsTitle}
              </h3>
              <Button
                variant="outline"
                size="small"
                icon={<Plus size={14} />}
                onPress={handleAddInput}
              >
                {t.processingTypesView.dialog.recipe.addInput}
              </Button>
            </div>

            {inputItems.length > 0 && (
              <div className="text-muted-foreground mb-2 grid grid-cols-[1fr_80px_1fr_40px] gap-2 px-3 text-xs">
                <span>
                  {t.processingTypesView.dialog.recipe.inputHeaders.item}
                </span>
                <span className="text-center">
                  {t.processingTypesView.dialog.recipe.inputHeaders.primary}
                </span>
                <span>
                  {t.processingTypesView.dialog.recipe.inputHeaders.notes}
                </span>
                <span />
              </div>
            )}

            <div className="space-y-2">
              {inputItems.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  {t.processingTypesView.dialog.recipe.emptyInputs}
                </p>
              ) : (
                inputItems.map((item, index) => (
                  <ProcessingTypeInputItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    onUpdate={handleUpdateInput}
                    onRemove={handleRemoveInput}
                    isDisabled={isSubmitting}
                  />
                ))
              )}
            </div>
          </div>

          <div className="border-t border-neutral/20 pt-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-medium">
                {t.processingTypesView.dialog.recipe.outputsTitle}
              </h3>
              <Button
                variant="outline"
                size="small"
                icon={<Plus size={14} />}
                onPress={handleAddOutput}
              >
                {t.processingTypesView.dialog.recipe.addOutput}
              </Button>
            </div>

            {outputItems.length > 0 && (
              <div className="text-muted-foreground mb-2 grid grid-cols-[1fr_100px_100px_80px_80px_100px_1fr_40px] gap-2 px-3 text-xs">
                <span>
                  {t.processingTypesView.dialog.recipe.outputHeaders.item}
                </span>
                <span>
                  {
                    t.processingTypesView.dialog.recipe.outputHeaders
                      .yieldPercentage
                  }
                </span>
                <span>
                  {t.processingTypesView.dialog.recipe.outputHeaders.outputUnit}
                </span>
                <span className="text-center">
                  {t.processingTypesView.dialog.recipe.outputHeaders.primary}
                </span>
                <span className="text-center">
                  {t.processingTypesView.dialog.recipe.outputHeaders.noCost}
                </span>
                <span>
                  {
                    t.processingTypesView.dialog.recipe.outputHeaders
                      .costPercent
                  }
                </span>
                <span>
                  {t.processingTypesView.dialog.recipe.outputHeaders.notes}
                </span>
                <span />
              </div>
            )}

            <div className="space-y-2">
              {outputItems.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  {t.processingTypesView.dialog.recipe.emptyOutputs}
                </p>
              ) : (
                outputItems.map((item, index) => (
                  <ProcessingTypeOutputItemRow
                    key={item.id}
                    item={item}
                    index={index}
                    onUpdate={handleUpdateOutput}
                    onRemove={handleRemoveOutput}
                    isDisabled={isSubmitting}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.processingTypesView.dialog.cancel}
          </Button>
          <Button
            icon={<Check />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
